import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { t } from "../lib/i18n";
import { toast } from "sonner";
import { Plus, X, Trophy, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import ImageUploader from "./admin/ImageUploader";

const MAX_PLAYER_OPTIONS = [4, 8, 16, 32, 64, 128];

export default function CreateTournament() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [bladers, setBladers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    location: "",
    description: "",
    start_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    prize_pool: "",
    prize_breakdown: [],
    banner_url: "",
    max_players: 8,
    format: "single_elim",
    num_groups: 2,
    advancers_per_group: 2,
    host_handle: "",
    existing_player_ids: [],
    new_player_handles: [],
    moderator_player_ids: [],
    scoring_format: "polish_bladers",
    score_limit: 5,
    knockout_score_limit: "",
  });
  const [newName, setNewName] = useState("");

  useEffect(() => {
    api.get("/players").then((r) => setBladers(r.data)).catch(() => {});
  }, []);

  const totalCount =
    form.existing_player_ids.length + form.new_player_handles.length;

  const togglePlayer = (id) => {
    setForm((f) => {
      const ex = f.existing_player_ids.includes(id);
      if (ex) return { ...f, existing_player_ids: f.existing_player_ids.filter((x) => x !== id) };
      if (totalCount >= form.max_players) {
        toast.error(`${t.rosterFull} (${form.max_players})`);
        return f;
      }
      return { ...f, existing_player_ids: [...f.existing_player_ids, id] };
    });
  };

  const addCustom = () => {
    const v = newName.trim();
    if (!v) return;
    if (totalCount >= form.max_players) {
      toast.error(`${t.rosterFull} (${form.max_players})`);
      return;
    }
    if (
      form.new_player_handles.some((h) => h.toLowerCase() === v.toLowerCase()) ||
      bladers
        .filter((b) => form.existing_player_ids.includes(b.id))
        .some((b) => b.handle.toLowerCase() === v.toLowerCase())
    ) {
      toast.error(t.alreadyAdded);
      return;
    }
    setForm({ ...form, new_player_handles: [...form.new_player_handles, v] });
    setNewName("");
  };

  const removeCustom = (h) =>
    setForm({ ...form, new_player_handles: form.new_player_handles.filter((x) => x !== h) });

  const submit = async (e) => {
    e.preventDefault();
    if (totalCount < 2) {
      toast.error(t.addAtLeastTwo);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        banner_url: form.banner_url || null,
        moderator_player_ids: form.moderator_player_ids || [],
        // Iter 33 — safety net: if the host tabbed away and left the score
        // limit input empty, fall back to the format default here too so
        // we never POST an empty string to the backend.
        score_limit: form.score_limit === "" || form.score_limit == null
          ? (form.format === "pokeblade" ? 6 : 5)
          : Math.max(1, parseInt(form.score_limit, 10) || 1),
        // Iter 34 — only persist the knockout override for group_knockout,
        // and only when the host actually typed something.
        knockout_score_limit: (
          form.format === "group_knockout" &&
          form.knockout_score_limit !== "" &&
          form.knockout_score_limit != null
        )
          ? Math.max(1, parseInt(form.knockout_score_limit, 10) || 1)
          : null,
      };
      const { data } = await api.post("/tournaments/community", payload);
      toast.success(t.created);
      nav(`/tournaments/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failedToCreate);
    } finally {
      setBusy(false);
    }
  };

  // Iter 33 — /host requires authentication. Anonymous users are bounced
  // to /login and returned here after sign-in. `user===null` means the
  // AuthProvider is still initializing; render nothing to avoid flicker.
  if (user === null) return null;
  if (!user) return <Navigate to="/login?next=/host" replace />;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
      <div className="border-b border-white/10 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono">
          // {t.hostEyebrow}
        </div>
        <h1 className="font-display font-black uppercase italic text-5xl sm:text-6xl mt-2">
          {t.hostTitle}
        </h1>
        <p className="text-white/60 mt-3 max-w-2xl">{t.hostIntro}</p>
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 border border-white/10 bg-[#0C0C14] p-6 space-y-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] font-mono">
            <Trophy size={12} /> {t.hostFormDetails}
          </div>
          <Field label={t.tournamentName} required value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            placeholder={t.tournamentNameSample} testid="tour-name" />
          <Field label={t.hostHandle} required value={form.host_handle}
            onChange={(v) => setForm({ ...form, host_handle: v })}
            placeholder={t.hostHandleSample} testid="tour-host" />
          <div className="grid grid-cols-2 gap-4">
            <Field label={t.location} value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              placeholder={t.locationSample} testid="tour-loc" />
            <Field label={t.prizePool} value={form.prize_pool}
              onChange={(v) => setForm({ ...form, prize_pool: v })}
              placeholder={t.prizePoolSample} testid="tour-prize" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field type="date" label={t.startDate} value={form.start_date}
              onChange={(v) => setForm({ ...form, start_date: v })} testid="tour-date" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
                {t.maxPlayers}
              </label>
              <div className="flex gap-1 flex-wrap" data-testid="tour-max-players">
                {MAX_PLAYER_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, max_players: n })}
                    className={`flex-1 min-w-[44px] py-2 text-xs font-bold uppercase tracking-[0.15em] border ${
                      form.max_players === n
                        ? "border-[#FF0055] text-[#FF0055] bg-[#FF0055]/10"
                        : "border-white/15 text-white/60 hover:text-white"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
              {t.description}
            </label>
            <textarea rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t.descriptionSample}
              className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]" />
          </div>
          <Field label={t.bannerUrl} value={form.banner_url}
            onChange={(v) => setForm({ ...form, banner_url: v })}
            placeholder="https://..." testid="tour-banner" />
          <div className="-mt-2">
            <ImageUploader
              onChange={(url) => setForm((f) => ({ ...f, banner_url: url }))}
              testid="tour-banner-upload"
              label={t.uploadBanner || t.uploadImage}
            />
          </div>
          {form.banner_url && (
            <div
              data-testid="tour-banner-preview"
              className="h-28 border border-white/10 bg-cover bg-center"
              style={{ backgroundImage: `url("${form.banner_url}")` }}
            />
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
              {t.format}
            </label>
            <select value={form.format}
              onChange={(e) => {
                const f = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  format: f,
                  ...(f === "pokeblade"
                    ? { scoring_format: "pokeblade", score_limit: 6 }
                    : f === "bey_pong"
                      ? { scoring_format: "bey_pong", score_limit: 4 }
                      : prev.scoring_format === "pokeblade" || prev.scoring_format === "bey_pong"
                        ? { scoring_format: "polish_bladers", score_limit: 5 }
                        : {}),
                }));
              }}
              data-testid="host-format-select"
              className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]">
              <option className="bg-[#0C0C14]" value="single_elim">{t.formatSingleElim}</option>
              <option className="bg-[#0C0C14]" value="double_elim">{t.formatDoubleElim}</option>
              <option className="bg-[#0C0C14]" value="group_knockout">{t.formatGroupKnockout}</option>
              <option className="bg-[#0C0C14]" value="pokeblade">{t.formatPokeblade}</option>
              <option className="bg-[#0C0C14]" value="bey_pong">{t.formatBeyPong}</option>
            </select>
          </div>
          {form.format === "group_knockout" && (
            <div className="grid grid-cols-2 gap-4">
              <Field type="number" label={t.numGroups} value={form.num_groups}
                onChange={(v) => setForm({ ...form, num_groups: parseInt(v) || 0 })} testid="host-num-groups" />
              <Field type="number" label={t.advancersPerGroup} value={form.advancers_per_group}
                onChange={(v) => setForm({ ...form, advancers_per_group: parseInt(v) || 1 })} testid="host-advancers" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
              {t.moderators}
            </label>
            {/* Iter 33 — multi-select moderators. Click a chip to toggle. */}
            <div
              data-testid="host-moderator-list"
              className="border border-white/15 p-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto"
            >
              {bladers.length === 0 && (
                <span className="text-[10px] font-mono text-white/40">— no bladers —</span>
              )}
              {bladers.map((b) => {
                const on = form.moderator_player_ids.includes(b.id);
                return (
                  <button
                    type="button"
                    key={b.id}
                    onClick={() => setForm((f) => ({
                      ...f,
                      moderator_player_ids: on
                        ? f.moderator_player_ids.filter((x) => x !== b.id)
                        : [...f.moderator_player_ids, b.id],
                    }))}
                    data-testid={`host-moderator-toggle-${b.id}`}
                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-[0.15em] border transition-colors ${
                      on
                        ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]"
                        : "border-white/15 text-white/60 hover:border-white/30"
                    }`}
                  >
                    {on ? "✓ " : ""}{b.handle}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] font-mono text-white/40 mt-1">
              {t.moderatorHint} · {form.moderator_player_ids.length} {t.selected}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
                {t.scoringFormat}
              </label>
              <select
                value={form.scoring_format}
                onChange={(e) => setForm({ ...form, scoring_format: e.target.value })}
                disabled={form.format === "pokeblade" || form.format === "bey_pong"}
                data-testid="host-scoring-format"
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] disabled:opacity-60"
              >
                <option value="polish_bladers" className="bg-[#0C0C14]">{t.scoringFormatPB}</option>
                <option value="beyblade_polska" className="bg-[#0C0C14]">{t.scoringFormatBP}</option>
                <option value="pokeblade" className="bg-[#0C0C14]">Pokeblade</option>
                <option value="bey_pong" className="bg-[#0C0C14]">BEY-PONG</option>
              </select>
              {/* Iter 40 — hint when combining group_knockout with BEY-PONG scoring */}
              {form.format === "group_knockout" && form.scoring_format === "bey_pong" && (
                <p
                  data-testid="beypong-groups-hint"
                  className="text-[10px] font-mono text-[#E2FF00] mt-1"
                >
                  ⓘ {t.beyPongGroupsHint}
                </p>
              )}
            </div>
            {/* Iter 33 — free-text numeric input for the score limit.
                Uses type=text + inputMode=numeric so browsers do NOT render
                the +/- spinner buttons but mobile keyboards still show the
                numeric keypad. Host is free to type any positive integer. */}
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
                {t.scoreLimit}
              </label>
              <input
                data-testid="host-score-limit"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={form.score_limit}
                onChange={(e) => {
                  // Keep digits only; empty string is allowed so the host can
                  // clear + retype without the field snapping back to 5.
                  const raw = e.target.value.replace(/[^0-9]/g, "");
                  setForm({ ...form, score_limit: raw === "" ? "" : Math.max(1, parseInt(raw, 10)) });
                }}
                onBlur={(e) => {
                  // Fall back to the format default only when the input is
                  // empty at blur time — never mid-typing.
                  if (e.target.value === "") {
                    setForm((f) => ({ ...f, score_limit: f.format === "pokeblade" ? 6 : 5 }));
                  }
                }}
                placeholder={form.format === "pokeblade" ? "6" : "5"}
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
              />
              <p className="text-[10px] font-mono text-white/40 mt-1">
                {t.scoreLimitHint}
              </p>
            </div>
            {/* Iter 34 — separate score threshold for the knockout stage
                of group_knockout tournaments (e.g. 7 in groups, 10 in
                knockout). Left blank → knockout inherits `score_limit`. */}
            {form.format === "group_knockout" && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
                  {t.knockoutScoreLimit}
                </label>
                <input
                  data-testid="host-knockout-score-limit"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  value={form.knockout_score_limit}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, "");
                    setForm({ ...form, knockout_score_limit: raw === "" ? "" : Math.max(1, parseInt(raw, 10)) });
                  }}
                  placeholder={String(form.score_limit || 5)}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
                />
                <p className="text-[10px] font-mono text-white/40 mt-1">
                  {t.knockoutScoreLimitHint}
                </p>
              </div>
            )}
          </div>
          {form.format === "pokeblade" && (
            <div
              data-testid="pokeblade-note"
              className="border border-[#9B7AFF]/30 bg-[#9B7AFF]/5 px-4 py-3 text-xs font-mono text-[#9B7AFF]"
            >
              ⓘ {t.pokebladeNote}
            </div>
          )}
          {form.format === "bey_pong" && (
            <div
              data-testid="beypong-note"
              className="border border-[#E2FF00]/30 bg-[#E2FF00]/5 px-4 py-3 text-xs font-mono text-[#E2FF00]"
            >
              ⓘ {t.beyPongNote}
            </div>
          )}
          <PrizeBreakdownEditor
            value={form.prize_breakdown}
            onChange={(v) => setForm({ ...form, prize_breakdown: v })}
          />
        </div>

        <div className="border border-white/10 bg-[#0C0C14] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] font-mono">
              <Sparkle size={12} /> {t.hostFormRoster}
            </div>
            <span
              data-testid="roster-count"
              className={`text-[10px] uppercase tracking-[0.25em] font-mono font-bold ${
                totalCount >= form.max_players ? "text-[#E2FF00]" : "text-white/50"
              }`}
            >
              {totalCount}/{form.max_players}
            </span>
          </div>

          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
              {t.addCustomBlader}
            </label>
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addCustom(); }
                }}
                placeholder={t.bladerName}
                data-testid="custom-blader-input"
                className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
              />
              <button
                type="button"
                onClick={addCustom}
                data-testid="add-custom-btn"
                className="px-3 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white"
              >
                <Plus size={16} weight="bold" />
              </button>
            </div>
            {form.new_player_handles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {form.new_player_handles.map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#FF0055]/40 text-[#FF0055] bg-[#FF0055]/5"
                  >
                    {h}
                    <button type="button" onClick={() => removeCustom(h)}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">
              {t.pickFromRoster}
            </label>
            <div className="max-h-72 overflow-y-auto border border-white/10 divide-y divide-white/5">
              {bladers.map((b) => {
                const checked = form.existing_player_ids.includes(b.id);
                return (
                  <label
                    key={b.id}
                    data-testid={`pick-${b.id}`}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-xs hover:bg-white/5 ${
                      checked ? "bg-[#00E5FF]/5" : ""
                    }`}
                  >
                    <input type="checkbox" checked={checked} onChange={() => togglePlayer(b.id)} />
                    <span className="flex-1 font-semibold">{b.handle}</span>
                    <span className="font-mono text-white/40">{b.country}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 flex flex-col gap-3 pt-2">
          <div
            data-testid="approval-note"
            className="border border-[#E2FF00]/30 bg-[#E2FF00]/5 px-4 py-3 text-xs font-mono text-[#E2FF00]"
          >
            ⓘ {t.approvalNote}
          </div>
          <div className="flex justify-end items-center gap-4">
            <p className="text-xs text-white/40 font-mono mr-auto">
              {t.bracketAutoNote}
            </p>
            <button
              type="submit"
              disabled={busy || totalCount < 2}
              data-testid="create-tournament-submit"
              className="px-7 py-3 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-sm disabled:opacity-40 glow-pink"
            >
              {busy ? t.creating : t.launchTournament}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">
        {label} {required && <span className="text-[#FF0055]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]"
      />
    </div>
  );
}

export function PrizeBreakdownEditor({ value, onChange }) {
  const rows = value || [];
  const update = (i, key, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: key === "place" ? parseInt(v) || (i + 1) : v };
    onChange(next);
  };
  const add = () => onChange([...rows, { place: rows.length + 1, item: "" }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div data-testid="prize-breakdown-editor">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">
          {t.prizeBreakdown}
        </label>
        <button type="button" onClick={add} data-testid="add-prize-row"
          className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00E5FF] hover:underline">
          {t.addPrizeRow}
        </button>
      </div>
      {rows.length === 0 && (
        <p className="text-[10px] text-white/30 font-mono">— {t.none} —</p>
      )}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`prize-row-${i}`}>
            <input type="number" value={r.place} onChange={(e) => update(i, "place", e.target.value)}
              className="col-span-2 bg-transparent border border-white/15 px-2 py-2 text-sm text-center focus:outline-none focus:border-[#00E5FF]" />
            <input value={r.item} onChange={(e) => update(i, "item", e.target.value)}
              placeholder={t.prizeItem}
              className="col-span-9 bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]" />
            <button type="button" onClick={() => remove(i)} className="col-span-1 text-[#FF0055]">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}
